import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Search, UserPlus, PhoneCall, ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { FormSection, FormField, StickyActionBar } from "@/components/common/FormSection";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

export default function NewHotlineSession() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=identify, 2=session
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [newPatient, setNewPatient] = useState({
    full_name: "", mobile: "", gender: "", dob: "", nationality: "", national_id: "", region: "", city: "",
  });
  const [registering, setRegistering] = useState(false);

  const [complaintCategories, setComplaintCategories] = useState([]);
  const [chief, setChief] = useState({ category: "", description: "", severity: "mild", duration: "" });
  const [priority, setPriority] = useState("routine");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/lookups/categories", { params: { page_size: 200 } }).then(async (cats) => {
      const cat = cats.data.items.find((c) => c.code === "complaint_categories");
      if (cat) {
        const v = await api.get("/lookups/values", { params: { category_id: cat.id } });
        setComplaintCategories(v.data.items);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.length < 1) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await api.get("/hotline/patients/search", { params: { q: query } });
        setResults(res.data.items || []);
      } catch (_e) { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  async function registerPatient() {
    if (!newPatient.full_name) { toast.error("Full name is required"); return; }
    setRegistering(true);
    try {
      const res = await api.post("/hotline/patients/register", newPatient);
      toast.success("Hotline patient registered");
      setSelectedPatient(res.data);
      setRegisterOpen(false);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRegistering(false); }
  }

  async function createSession() {
    if (!selectedPatient) { toast.error("Pick a patient first."); return; }
    setCreating(true);
    try {
      const res = await api.post("/hotline/sessions", {
        patient_id: selectedPatient.id,
        chief_complaint: chief,
        priority,
      });
      toast.success(`Session ${res.data.record_id} created`);
      navigate(`/hotline/sessions/${res.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCreating(false); }
  }

  return (
    <div data-testid="hotline-new-page">
      <PageHeader
        title="New Hotline Session"
        subtitle={step === 1 ? "Step 1 of 2 — identify the patient calling in." : "Step 2 of 2 — capture chief complaint and start the session."}
        actions={
          <Button variant="outline" onClick={() => navigate(-1)} className="border-[#E5E7EB]" data-testid="hotline-back">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
        }
      />

      {step === 1 && (
        <>
          <FormSection title="Patient Identification" testId="hotline-step-identify" description="Search by MRN, name, mobile, or national ID. If no result, register a hotline-only patient.">
            <FormField label="Search" full>
              <div className="relative">
                <div className="flex items-center gap-2 bg-white border border-[#E5E7EB] rounded-lg px-3 py-2.5">
                  <Search className="w-4 h-4 text-[#94A3B8]" />
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search patients…"
                    className="bg-transparent outline-none text-sm flex-1 text-[#1F2937] placeholder-[#94A3B8]"
                    data-testid="hotline-patient-search"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setRegisterOpen(true)}
                    className="border-[#0B7E8C] text-[#0B7E8C] hover:bg-[#EAF8FA]"
                    data-testid="hotline-register-new"
                  >
                    <UserPlus className="w-4 h-4 mr-1.5" /> Register new
                  </Button>
                </div>
                {results.length > 0 && (
                  <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-md max-h-80 overflow-y-auto">
                    {results.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => { setSelectedPatient(p); setResults([]); setQuery(`${p.first_name} ${p.last_name}`); }}
                        data-testid={`hotline-result-${p.id}`}
                        className="w-full text-left px-3 py-2 hover:bg-[#EAF8FA]/50 flex items-center gap-3 border-b border-[#E5E7EB] last:border-b-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-[#EAF8FA] text-[#0B7E8C] flex items-center justify-center text-xs font-semibold">
                          {(p.first_name?.[0] || "?") + (p.last_name?.[0] || "")}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-[#1F2937] truncate">{p.first_name} {p.last_name}</div>
                          <div className="text-xs text-[#6B7280]">
                            {p.mrn} · {p.patient_type === "hotline_only" ? "Hotline-only" : "H@H"} · {p.phone || "no phone"}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </FormField>

            {selectedPatient && (
              <FormField label="Selected patient" full>
                <div className="flex items-center gap-3 p-3 border border-[#0B7E8C]/20 rounded-lg bg-[#EAF8FA]/40">
                  <div className="w-10 h-10 rounded-full bg-[#0B7E8C] text-white flex items-center justify-center font-semibold text-sm">
                    {(selectedPatient.first_name?.[0] || "?") + (selectedPatient.last_name?.[0] || "")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-[#1F2937]">{selectedPatient.first_name} {selectedPatient.last_name}</div>
                    <div className="text-xs text-[#6B7280]">
                      {selectedPatient.mrn} · DOB {selectedPatient.dob || "—"} · {selectedPatient.phone || "—"} · {selectedPatient.patient_type === "hotline_only" ? "Hotline-only" : "H@H"}
                    </div>
                  </div>
                  <Button onClick={() => setSelectedPatient(null)} variant="outline" size="sm" className="border-[#E5E7EB]">
                    Change
                  </Button>
                </div>
              </FormField>
            )}
          </FormSection>

          <StickyActionBar>
            <Button variant="outline" onClick={() => navigate("/hotline")} className="border-[#E5E7EB]">Cancel</Button>
            <Button
              onClick={() => setStep(2)}
              disabled={!selectedPatient}
              className="bg-[#0B7E8C] hover:bg-[#128C97] text-white"
              data-testid="hotline-step-next"
            >
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </StickyActionBar>
        </>
      )}

      {step === 2 && selectedPatient && (
        <>
          <FormSection title="Chief Complaint" testId="hotline-step-complaint">
            <FormField label="Complaint category">
              <Select value={chief.category} onValueChange={(v) => setChief({ ...chief, category: v })}>
                <SelectTrigger data-testid="hotline-cc-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {complaintCategories.map((c) => <SelectItem key={c.id} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Severity">
              <Select value={chief.severity} onValueChange={(v) => setChief({ ...chief, severity: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="mild">Mild</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="severe">Severe</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Duration">
              <Input value={chief.duration} placeholder="e.g., 2 hours" onChange={(e) => setChief({ ...chief, duration: e.target.value })} data-testid="hotline-cc-duration" />
            </FormField>
            <FormField label="Priority">
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="hotline-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="routine">Routine</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                  <SelectItem value="stat">STAT</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Description" full>
              <Textarea
                rows={3}
                value={chief.description}
                onChange={(e) => setChief({ ...chief, description: e.target.value })}
                placeholder="Describe the chief complaint in patient's own words…"
                data-testid="hotline-cc-description"
              />
            </FormField>
          </FormSection>

          <StickyActionBar>
            <Button variant="outline" onClick={() => setStep(1)} className="border-[#E5E7EB]">Back</Button>
            <Button onClick={createSession} disabled={creating} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-create-session">
              <PhoneCall className="w-4 h-4 mr-1.5" /> {creating ? "Creating…" : "Create session"}
            </Button>
          </StickyActionBar>
        </>
      )}

      {/* Register patient sheet */}
      {registerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRegisterOpen(false)}>
          <div className="bg-white rounded-xl max-w-xl w-full p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-[#1F2937] mb-1">Register hotline patient</h3>
            <p className="text-sm text-[#6B7280] mb-4">Quick registration for a new hotline-only patient. They'll be saved into Patient Management.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder="Full name *" value={newPatient.full_name} onChange={(e) => setNewPatient({ ...newPatient, full_name: e.target.value })} data-testid="hotline-reg-name" />
              <Input placeholder="Mobile" value={newPatient.mobile} onChange={(e) => setNewPatient({ ...newPatient, mobile: e.target.value })} />
              <Input type="date" placeholder="DOB" value={newPatient.dob} onChange={(e) => setNewPatient({ ...newPatient, dob: e.target.value })} />
              <Select value={newPatient.gender} onValueChange={(v) => setNewPatient({ ...newPatient, gender: v })}>
                <SelectTrigger><SelectValue placeholder="Gender" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
              <Input placeholder="Nationality" value={newPatient.nationality} onChange={(e) => setNewPatient({ ...newPatient, nationality: e.target.value })} />
              <Input placeholder="National ID" value={newPatient.national_id} onChange={(e) => setNewPatient({ ...newPatient, national_id: e.target.value })} />
              <Input placeholder="Region" value={newPatient.region} onChange={(e) => setNewPatient({ ...newPatient, region: e.target.value })} />
              <Input placeholder="City" value={newPatient.city} onChange={(e) => setNewPatient({ ...newPatient, city: e.target.value })} />
            </div>
            <div className="flex items-center justify-end gap-2 mt-5">
              <Button variant="outline" onClick={() => setRegisterOpen(false)} className="border-[#E5E7EB]">Cancel</Button>
              <Button onClick={registerPatient} disabled={registering} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="hotline-reg-save">
                {registering ? "Saving…" : "Register & continue"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
